import React, { useState, useEffect } from 'react';
import { Card, Flex, Text, Button, Badge, Metric, Title } from '@tremor/react';
import { 
    LightBulbIcon, 
    ExclamationTriangleIcon, 
    EyeIcon,
    ArrowTrendingUpIcon,
    InformationCircleIcon,
    ClockIcon,
    ArrowPathIcon,
    VideoCameraIcon,
    SpeakerWaveIcon,
    StarIcon,
    FaceSmileIcon,
    FaceFrownIcon,
    ExclamationCircleIcon,
    ScaleIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { getEmotionInsights, triggerEmotionInsightsAnalysis } from '../../Requests/EmotionInsightsRequests';

// Custom Tooltip Component
function CustomTooltip({ children, tooltip }) {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div 
            className="relative"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            {children}
            {showTooltip && tooltip && (
                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-50 w-80 max-w-sm break-words">
                    {tooltip}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
}

function EmotionInsightsCard({ analysisParentUuid, ...rest }) {
    const { t } = useTranslation();
    const [insightsData, setInsightsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isTriggering, setIsTriggering] = useState(false);
    
    // Helper function to detect emotion contrasts between video and audio
    const getEmotionContrast = (timepoint) => {
        if (!timepoint.primary_emotions) return null;
        
        const videoEmotions = timepoint.primary_emotions.filter(e => e.source === 'video');
        const audioEmotions = timepoint.primary_emotions.filter(e => e.source === 'audio');
        
        if (videoEmotions.length === 0 || audioEmotions.length === 0) return null;
        
        const topVideo = videoEmotions[0];
        const topAudio = audioEmotions[0];
        
        // Simple contrast detection - if top emotions are very different
        const isContrast = topVideo.emotion !== topAudio.emotion;
        
        return isContrast ? {
            video: topVideo,
            audio: topAudio,
            hasContrast: true
        } : null;
    };

    // Helper function to get icon and color for insight types
    const getInsightTypeIcon = (insightType) => {
        switch (insightType) {
            case 'baseline':
                return { icon: ClockIcon, color: 'text-gray-500', bgColor: 'bg-gray-50', label: 'Baseline' };
            case 'positive_peak':
                return { icon: FaceSmileIcon, color: 'text-gray-500', bgColor: 'bg-gray-50', label: 'Positive Peak' };
            case 'negative_peak':
                return { icon: FaceFrownIcon, color: 'text-gray-500', bgColor: 'bg-gray-50', label: 'Negative Peak' };
            case 'video_audio_contrast':
                return { icon: ScaleIcon, color: 'text-gray-500', bgColor: 'bg-gray-50', label: 'Video/Audio Contrast' };
            case 'emotional_transition':
                return { icon: ArrowTrendingUpIcon, color: 'text-gray-500', bgColor: 'bg-gray-50', label: 'Emotional Transition' };
            case 'key_moment':
                return { icon: StarIcon, color: 'text-gray-500', bgColor: 'bg-gray-50', label: 'Key Moment' };
            default:
                return { icon: LightBulbIcon, color: 'text-gray-500', bgColor: 'bg-gray-50', label: 'Important Moment' };
        }
    };

    // Helper function to extract and format insight type from emotional_change text
    const extractInsightType = (emotionalChange) => {
        if (!emotionalChange) return null;
        
        // Look for patterns like "NEGATIVE PEAK:", "POSITIVE PEAK:", "BASELINE:", etc.
        const match = emotionalChange.match(/^([A-Z\s]+):/);
        if (match) {
            const rawType = match[1].trim();
            // Convert to proper case - first letter uppercase, rest lowercase
            return rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase();
        }
        return null;
    };

    // Helper function to remove insight type prefix from emotional_change text
    const removeInsightTypeFromText = (emotionalChange) => {
        if (!emotionalChange) return emotionalChange;
        
        // Remove patterns like "NEGATIVE PEAK:", "POSITIVE PEAK:", "BASELINE:", etc.
        return emotionalChange.replace(/^[A-Z\s]+:\s*/, '').trim();
    };
    
    // Fetch insights data
    const fetchInsights = async () => {
        try {
            setLoading(true);
            setError(null);
            
            console.log('DEBUG: Fetching emotion insights for:', analysisParentUuid);
            const response = await getEmotionInsights(analysisParentUuid);
            console.log('DEBUG: Insights response:', response);
            
            setInsightsData(response);
            
        } catch (error) {
            console.error('DEBUG: Error fetching emotion insights:', error);
            console.error('DEBUG: Error details:', error.response && error.response.data);
            setError(error.response && error.response.data && error.response.data.message || t('EmotionInsights.ErrorFetchingInsights'));
        } finally {
            setLoading(false);
        }
    };

    // Trigger analysis manually
    const handleTriggerAnalysis = async () => {
        try {
            setIsTriggering(true);
            setError(null);
            
            console.log('DEBUG: Triggering emotion insights analysis for:', analysisParentUuid);
            await triggerEmotionInsightsAnalysis(analysisParentUuid);
            
            // Clear current data to show fresh results
            setInsightsData(null);
            
            // Poll for results after triggering with multiple checks
            setTimeout(() => fetchInsights(), 3000);  // First check after 3s
            setTimeout(() => fetchInsights(), 8000);  // Second check after 8s
            setTimeout(() => fetchInsights(), 15000); // Third check after 15s
            
        } catch (error) {
            console.error('DEBUG: Error triggering emotion insights analysis:', error);
            setError(error.response && error.response.data && error.response.data.message || t('EmotionInsights.ErrorTriggeringAnalysis'));
        } finally {
            setIsTriggering(false);
        }
    };

    useEffect(() => {
        if (analysisParentUuid) {
            fetchInsights();
        }
    }, [analysisParentUuid]);

    // Format time range for display
    const formatTimeRange = (timeRange) => {
        if (!timeRange || typeof timeRange !== 'string') return '';
        
        const parts = timeRange.split('-');
        if (parts.length !== 2) return timeRange;
        
        const start = parseFloat(parts[0]);
        const end = parseFloat(parts[1]);
        
        const formatTime = (seconds) => {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        };
        
        return `${formatTime(start)} - ${formatTime(end)}`;
    };

    // Get confidence badge color
    const getConfidenceBadgeColor = (confidence) => {
        if (!confidence) return 'gray';
        const conf = confidence.toLowerCase();
        if (conf === 'alta' || conf === 'high') return 'green';
        if (conf === 'média' || conf === 'media' || conf === 'medium') return 'yellow';
        return 'red';
    };

    // Get significance badge color
    const getSignificanceBadgeColor = (significance) => {
        if (!significance) return 'gray';
        const sig = significance.toLowerCase();
        if (sig === 'alta' || sig === 'high') return 'red';
        if (sig === 'média' || sig === 'media' || sig === 'medium') return 'yellow';
        return 'gray';
    };

    // Get tension level badge color
    const getTensionBadgeColor = (level) => {
        if (!level) return 'gray';
        const lvl = level.toLowerCase();
        if (lvl === 'alta' || lvl === 'high') return 'red';
        if (lvl === 'média' || lvl === 'media' || lvl === 'medium') return 'orange';
        return 'yellow';
    };

    // Don't render if no data and not processing
    if (!insightsData && !loading && (!error || error.includes('not_available'))) {
        return null;
    }

    return (
        <Card className="mt-4" {...rest}>
            <div>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full border border-gray-300 bg-white">
                            <LightBulbIcon className="w-4 h-4 text-gray-700" />
                        </div>
                        <Title className="text-lg font-semibold">
                            {t('EmotionInsights.Title')}
                        </Title>
                        <CustomTooltip tooltip={t('EmotionInsights.HowItWorks.Description')}>
                            <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                        </CustomTooltip>
                    </div>
                    {/* Debug Button - Always visible for testing */}
                    <Button
                        size="xs"
                        onClick={handleTriggerAnalysis}
                        loading={isTriggering}
                        className="bg-gray-500 hover:bg-gray-600 text-white"
                    >
                        <div className="flex items-center">
                            <ArrowPathIcon className="w-3 h-3 mr-1" />
                            Debug: Regenerar
                        </div>
                    </Button>
                </div>

            {loading && (
                <Flex alignItems="center" className="py-8">
                    <ClockIcon className="w-5 h-5 text-gray-400 mr-2" />
                    <Text>{t('EmotionInsights.LoadingInsights')}</Text>
                </Flex>
            )}

            {error && (
                <Flex direction="col" alignItems="center" className="py-8">
                    <ExclamationTriangleIcon className="w-8 h-8 text-red-500 mb-2" />
                    <Text className="text-red-600 text-center mb-4">{error}</Text>
                    {(error.includes('not_available') || error.includes('not yet started')) && (
                        <Button
                            onClick={handleTriggerAnalysis}
                            loading={isTriggering}
                            className="bg-amber-500 hover:bg-amber-600"
                        >
                            {t('EmotionInsights.TriggerAnalysis')}
                        </Button>
                    )}
                </Flex>
            )}

            {insightsData && insightsData.status === 'processing' && (
                <Flex alignItems="center" className="py-8">
                    <ClockIcon className="w-5 h-5 text-blue-400 mr-2 animate-spin" />
                    <Text className="text-blue-600">{insightsData.message || t('EmotionInsights.ProcessingInsights')}</Text>
                </Flex>
            )}

            {insightsData && insightsData.status === 'completed' && insightsData.insights && (
                <div className="space-y-6">
                    {/* Key Emotional Moments View */}
                    {insightsData.insights.timeline_analysis && insightsData.insights.timeline_analysis.length > 0 ? (
                        <div className="overflow-x-auto rounded-lg bg-white shadow-sm border border-gray-100">
                            <table className="min-w-full">
                                <thead className="bg-white border-b border-gray-100">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('EmotionInsights.Time')}
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('EmotionInsights.PrimaryEmotions')}
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('EmotionInsights.EmotionalChange')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-50 bg-white">
                                    {insightsData.insights.timeline_analysis.map((timepoint, index) => {
                                        const insightTypeInfo = getInsightTypeIcon(timepoint.insight_type);
                                        const IconComponent = insightTypeInfo.icon;
                                        
                                        return (
                                            <tr key={index} className="hover:bg-gray-50/30">
                                                <td className="px-4 py-4 text-sm">
                                                    <div className="font-medium text-gray-900">
                                                        {timepoint.time_start}s - {timepoint.time_end}s
                                                    </div>
                                                    {timepoint.speaker && (
                                                        <div className="text-xs text-gray-500 mt-1">
                                                            {timepoint.speaker}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-4 text-sm">
                                                    <div className="space-y-3">
                                                        {/* Video Emotions */}
                                                        {timepoint.primary_emotions && timepoint.primary_emotions.filter(e => e.source === 'video').length > 0 && (
                                                            <div>
                                                                <div className="flex items-center space-x-1 mb-1">
                                                                    <VideoCameraIcon className="w-3 h-3 text-gray-500" />
                                                                    <Text className="text-xs font-medium text-gray-600">Video</Text>
                                                                </div>
                                                                <div className="space-y-1 pl-4">
                                                                    {timepoint.primary_emotions
                                                                        .filter(e => e.source === 'video')
                                                                        .slice(0, 3)
                                                                        .map((emotion, idx) => (
                                                                            <div key={idx} className="flex justify-between items-center">
                                                                                <Text className="text-xs text-gray-700">{emotion.emotion}</Text>
                                                                                <span className="text-xs text-gray-500">{emotion.confidence}</span>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </div>
                                                        )}
                                                        
                                                        {/* Audio Emotions */}
                                                        {timepoint.primary_emotions && timepoint.primary_emotions.filter(e => e.source === 'audio').length > 0 && (
                                                            <div>
                                                                <div className="flex items-center space-x-1 mb-1">
                                                                    <SpeakerWaveIcon className="w-3 h-3 text-gray-500" />
                                                                    <Text className="text-xs font-medium text-gray-600">Audio</Text>
                                                                </div>
                                                                <div className="space-y-1 pl-4">
                                                                    {timepoint.primary_emotions
                                                                        .filter(e => e.source === 'audio')
                                                                        .slice(0, 3)
                                                                        .map((emotion, idx) => (
                                                                            <div key={idx} className="flex justify-between items-center">
                                                                                <Text className="text-xs text-gray-700">{emotion.emotion}</Text>
                                                                                <span className="text-xs text-gray-500">{emotion.confidence}</span>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-700">
                                                    <div className="space-y-2">
                                                        {extractInsightType(timepoint.emotional_change) && (
                                                            <span className="inline-block px-2 py-1 text-xs font-medium text-white bg-iavisus-100 rounded-md mb-2">
                                                                {extractInsightType(timepoint.emotional_change)}
                                                            </span>
                                                        )}
                                                        <Text className="text-sm">
                                                            {removeInsightTypeFromText(timepoint.emotional_change)}
                                                        </Text>
                                                        
                                                        {timepoint.relevant_quote && (
                                                            <Text className="text-xs italic text-gray-500 border-l-2 border-gray-300 pl-2">
                                                                "{timepoint.relevant_quote}"
                                                            </Text>
                                                        )}
                                                        
                                                        {timepoint.possible_cause && (
                                                            <Text className="text-xs text-gray-500">
                                                                <span className="font-medium">Cause:</span> {timepoint.possible_cause}
                                                            </Text>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <Text className="text-gray-500 mb-2">
                                {t('EmotionInsights.NoTimelineDataFound')}
                            </Text>
                            {/* Debug info */}
                            <Text className="text-xs text-gray-400 bg-gray-50 p-2 rounded">
                                Debug: Status = {insightsData && insightsData.status}, 
                                Insights keys = {insightsData && insightsData.insights ? Object.keys(insightsData.insights).join(', ') : 'none'},
                                Timeline = {insightsData && insightsData.insights && insightsData.insights.timeline_analysis ? 
                                    `${insightsData.insights.timeline_analysis.length} items` : 'null'
                                }
                            </Text>
                        </div>
                    )}
                    
                    {/* Analysis Summary */}
                    {insightsData.insights.summary && (
                        <div className="space-y-4">
                            {/* Significant Changes */}
                            <div>
                                <Text className="text-sm font-medium text-gray-700 mb-2">{t('EmotionInsights.SignificantChanges')}</Text>
                                <Text className="text-sm text-gray-600">{insightsData.insights.summary.significant_changes}</Text>
                            </div>
                            
                            {/* Emotional Stability */}
                            <div>
                                <Text className="text-sm font-medium text-gray-700 mb-2">{t('EmotionInsights.EmotionalStability')}</Text>
                                <Badge 
                                    color={
                                        insightsData.insights.summary.emotional_stability === 'alta' || insightsData.insights.summary.emotional_stability === 'high' ? 'green' :
                                        insightsData.insights.summary.emotional_stability === 'média' || insightsData.insights.summary.emotional_stability === 'medium' ? 'yellow' : 'red'
                                    }
                                >
                                    {insightsData.insights.summary.emotional_stability}
                                </Badge>
                            </div>
                            
                            {/* Most Common Emotions */}
                            <div>
                                <Text className="text-sm font-medium text-gray-700 mb-2">{t('EmotionInsights.MostCommonEmotions')}</Text>
                                <Flex className="space-x-1 flex-wrap">
                                    {insightsData.insights.summary.most_common_emotions && insightsData.insights.summary.most_common_emotions.map((emotion, idx) => (
                                        <Badge key={idx} color="gray" size="xs">{emotion}</Badge>
                                    ))}
                                </Flex>
                            </div>
                            
                            {/* Key Insights */}
                            {insightsData.insights.summary.key_insights && insightsData.insights.summary.key_insights.length > 0 && (
                                <div>
                                    <Text className="text-sm font-medium text-gray-700 mb-2">{t('EmotionInsights.KeyInsights')}</Text>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        {insightsData.insights.summary.key_insights.map((insight, idx) => (
                                            <li key={idx} className="flex items-start">
                                                <span className="text-gray-400 mr-2">•</span>
                                                <span>{insight}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
            </div>
        </Card>
    );
}

export default EmotionInsightsCard;